import { useCallback, useEffect } from 'react';
import { Box, Group, SimpleGrid, UnstyledButton } from '@mantine/core';
import { IconBackspace } from '@tabler/icons-react';

// Four digits, entered the way a child enters them: big keys, one hand, no
// keyboard. It auto-submits on the fourth digit, so there is no "OK" to find.
//
// A hardware keyboard works too — desktop, and anyone using the tablet with a
// keyboard for accessibility reasons — because a keypad that only takes taps
// is a keypad some people cannot use.

export function Keypad({
  pin,
  onChange,
  onComplete,
  color,
  disabled,
}: {
  pin: string;
  onChange: (pin: string) => void;
  onComplete: (pin: string) => void;
  color: string;
  disabled?: boolean;
}) {
  const add = useCallback(
    (d: string) => onChange(pin.length >= 4 ? pin : pin + d),
    [pin, onChange],
  );
  const del = useCallback(() => onChange(pin.slice(0, -1)), [pin, onChange]);

  useEffect(() => {
    if (pin.length === 4) onComplete(pin);
  }, [pin, onComplete]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled) return;
      if (/^[0-9]$/.test(e.key)) add(e.key);
      else if (e.key === 'Backspace') del();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [add, del, disabled]);

  return (
    <>
      <Group gap={16} aria-label="PIN-Stellen">
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            style={{
              width: 16,
              height: 16,
              borderRadius: 999,
              border: `2px solid var(--mantine-color-${color}-6)`,
              background: i < pin.length ? `var(--mantine-color-${color}-6)` : 'transparent',
              transition: 'background 120ms ease',
            }}
          />
        ))}
      </Group>

      <SimpleGrid cols={3} spacing="sm" w="100%">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((k, i) => {
          if (k === '') return <Box key={i} />;
          const isDel = k === '⌫';
          return (
            <UnstyledButton
              key={i}
              onClick={() => (isDel ? del() : add(k))}
              disabled={disabled}
              aria-label={isDel ? 'Löschen' : k}
              style={{
                aspectRatio: '1.35',
                borderRadius: 'var(--mantine-radius-lg)',
                background: isDel ? 'transparent' : 'var(--mantine-color-white)',
                border: isDel ? 'none' : '1px solid var(--mantine-color-gray-2)',
                boxShadow: isDel ? undefined : 'var(--mantine-shadow-xs)',
                color: isDel ? 'var(--mantine-color-gray-6)' : 'var(--mantine-color-gray-9)',
                fontSize: 26,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isDel ? <IconBackspace size={24} /> : k}
            </UnstyledButton>
          );
        })}
      </SimpleGrid>
    </>
  );
}
